// @flow
import * as React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { connectAPI, withConfirmation } from 'speed-js-react';
import HorizontalLine from 'esm-web-pro/components/layout/HorizontalLine';
import MainActions from 'esm-web-pro/components/layout/sidePanel/MainActions';
import TimelineIcon from 'esm-web-pro/components/layout/sidePanel/icons/TimelineIcon';
import EditIcon from 'esm-web-pro/components/layout/sidePanel/icons/EditIcon';
import DeleteIcon from 'esm-web-pro/components/layout/sidePanel/icons/DeleteIcon';
import {
    defineMessages, FormattedMessage, injectIntl, IntlShape,
} from 'react-intl';
import type { LeafletContext } from 'react-leaflet/lib/types';
import { withLeaflet } from 'react-leaflet';
import { withRouter } from 'react-router';
import type { RouterHistory, Match } from 'react-router-dom';
import type { Map } from 'esm-web-pro/containers/map/types';
import type { Coverage } from 'esm-web-pro/types/coverages';
import CoverageEditor from 'esm-web-pro/containers/pages/coverage/CoverageEditor';
import { Box } from 'grommet';
import BaseEditor from 'esm-web-pro/containers/map/editor/BaseEditor';
import UpperContainer from 'esm-web-pro/components/layout/sidePanel/UpperContainer';
import actionMessages from 'esm-web-pro/messages/actionMessages';
import UseHighlightPolygon from 'esm-web-pro/hooks/useHighlightPolygon';
import * as sidePanelActions from 'esm-web-pro/redux/side-panel/actions';
import { COVERAGE_EDIT_KEY } from 'esm-web-pro/containers/pages/coverage/Util';
import type { Confirmation } from 'speed-js-react';
import NavigateBackButton from 'esm-web-pro/components/maps/NavigateBackButton';
import SidePane from 'esm-web-pro/components/layout/SidePane';


type Props = {
    panelId: String,
    object: Coverage,
    intl: IntlShape,
    history: RouterHistory,
    match: Match,
    map: Map,
    editor: BaseEditor,
    updateObject: (data: Object) => void,
    leaflet: LeafletContext,
    deleteCoverage: Function,
    confirm: (confirmation: Confirmation) => void,
    openPanel: (key: string, url: ?string, sensitivity?: boolean) => { key: string, url: string },
    closePanel: (key: string, skipSensitivity?: boolean) => { key: string },
}

const msg = defineMessages({
    deleteCoverage: {
        id: 'delete-coverage-title',
        defaultMessage: 'Are you sure you want to delete the coverage?',
    },
    cancel: {
        id: 'cancel-delete-coverage',
        defaultMessage: 'Cancel',
    },
    confirm: {
        id: 'confirm-delete-coverage',
        defaultMessage: 'Confirm',
    },
});

const CoverageComponent = (props: Props) => {
    const {
        intl: { formatMessage },
        history,
        match,
        match: { params: { objectType } },
        object,
        map: { is_draft: isDraft, uuid: mapUuid },
        map,
        editor,
        updateObject,
        leaflet,
        deleteCoverage,
        closePanel,
        openPanel,
        panelId,
    } = props;

    const handleEditClose = (forceClose: boolean = false) => {
        closePanel(COVERAGE_EDIT_KEY, forceClose);
    };

    const coordinates = object.polygon.coordinates[0].map((c) => [c[1], c[0]]);

    const handleFocus = () => {
        const highlightPolygon = UseHighlightPolygon(
            coordinates,
            [{
                weight: 6, dashArray: '2%', className: 'polygon_line',
            },
            {
                weight: 2,
                dashArray: null,
                className: '',
            }],
        );
        editor.registerLoadingCallback(highlightPolygon);
        leaflet.map.highlightedGeometry = {
            bounds: coordinates, center: null,
        };
        leaflet.map.panInsideBounds(coordinates);
        editor.reloadFeatures();
    };

    const memoizedFocus = React.useCallback(handleFocus, [object]);

    React.useEffect(() => {
        memoizedFocus();
        return () => {
            editor.unregisterAllLoadingCallback();
        };
    }, [memoizedFocus, editor]);

    const navigateBack = () => {
        props.history.push(props.match.url.split('/layer')[0]);
    };

    const onDeleteCoverage = () => {
        const { confirm } = props;
        confirm({
            message: formatMessage(msg.deleteCoverage),
            actions: {
                cancel: {
                    label: formatMessage(msg.cancel),
                    callback: () => {},
                    type: 'negative',
                },
                confirm: {
                    label: formatMessage(msg.confirm),
                    callback: () => {
                        deleteCoverage(mapUuid, object.identity).then(navigateBack);
                    },
                    type: 'positive',
                },
            },
        });
    };

    // used to replace the slash at the end of the url (if present)
    const regex = /^(.+?)(\/+)$/gm;
    const actions = [
        {
            key: `${object.uuid}-timeline`,
            icon: <TimelineIcon />,
            title: formatMessage(actionMessages.timeline),
            callback: () => history.push(`${match.url.replace(regex, '$1')}/timeline/coverage/${object.identity}`),
            color: 'transparent',
        },
    ];
    if (isDraft && !object.valid_to) {
        actions.push({
            key: `${object.uuid}-edit`,
            icon: <EditIcon />,
            title: formatMessage(actionMessages.edit),
            callback: () => openPanel(COVERAGE_EDIT_KEY, '', match.url, true),
            color: 'transparent',
        }, {
            key: `${object.uuid}-delete`,
            icon: <DeleteIcon />,
            title: formatMessage(actionMessages.delete),
            callback: () => onDeleteCoverage(),
            color: 'transparent',
        });
    }

    return (
        <SidePane
            id={panelId}
            openInitially
            closeAllOpened
            title={object.name}
            titleLabel={<FormattedMessage id="sidebar-network-coverage" defaultMessage="Coverage" />}
            actions={<NavigateBackButton panelId={panelId} />}
        >
            <Box height="100%" justify="between">
                <Box>
                    <UpperContainer>
                        <HorizontalLine color="grey" />
                        <Box direction="row" pad={{ vertical: '15px' }}>
                            <MainActions main actions={actions} />
                        </Box>
                        <CoverageEditor
                            context={map}
                            editor={editor}
                            onClose={handleEditClose}
                            resource={
                                editor.props.resources.find((resource) => resource.sidePaneResource === objectType)}
                            handleUpdateObject={updateObject}
                            object={object}
                        />
                        <HorizontalLine color="grey" />
                    </UpperContainer>
                </Box>
            </Box>
        </SidePane>

    );
};

// Coverage.defaultProps

const apiCallback = (apiClient) => ({
    deleteCoverage: {
        send: (mapUuid, id) => apiClient.resources['coverage.geo'].delete(mapUuid, id),
    },
});

const mapDispatchToProps = (dispatch) => (
    bindActionCreators(sidePanelActions, dispatch)
);

export default connect(null, mapDispatchToProps)(
    connectAPI(apiCallback)(
        withLeaflet(
            injectIntl(
                withRouter(
                    withConfirmation(
                        CoverageComponent,
                    ),
                ),
            ),
        ),
    ),
);
